import PropTypes from "prop-types";
import axios from "axios";
import {
  Button,
  Card,
  Col,
  Flex,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  notification,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import "./SelectDocument.css";
import Link from "antd/es/typography/Link";
import { useNavigate } from "react-router-dom";

import { PdfViewerModal } from "../pdf-viewer-modal/PdfViewerModal";
import { useSettingsStore } from "../../store/settings-store";
import { useAxiosPrivate } from "../../../../../../hooks/useAxiosPrivate";
import { useSessionStore } from "../../../../../../store/session-store";
import { useAlertStore } from "../../../../../../store/alert-store";
import { SampleDocCard } from "../sample-doc-card/SampleDocCard";
import { TopBar } from "../../../../../../components/widgets/top-bar/TopBar";
import {
  ChaseCCStatement,
  FormThumbnail,
  HandwrittenScannedLoanForm,
  OffOrientationDLScan,
  SampleAcordForm,
  SBCSample,
  SmartphoneCapturedReceipt,
  AnnotatedChaseCCStatement,
} from "../../../../../assets";
import { LLM_WHISPERER_ENDPOINT } from "../../../../helper";

const docs = [
  {
    id: 1,
    docName: "Electronically filled PDF form",
    description:
      "Extract electronically filled out form information including selected radiobuttons and checkboxes are a very common use case in several businesses.",
    docLink: "1003_form.pdf",
    modeUsed: "High Quality with Form Elements",
    modeId: "form",
    thumbnail: FormThumbnail,
  },
  {
    id: 2,
    docName: "Handwritten forms",
    description:
      "Extract handwritten form information including hand-checked radiobuttons and checkboxes are still pretty common in several countries.",
    docLink: "Handwritten_Scanned_Loan_Form.pdf",
    modeUsed: "High Quality with Form Elements",
    modeId: "form",
    thumbnail: HandwrittenScannedLoanForm,
  },
  {
    id: 3,
    docName: "Mixed printed and handwritten document",
    description:
      "This is a fairly common real-world use case that LLMWhisperer handles with ease. LLMWhisperer can easily make out the difference between printed text and handwritten text, but deal with both in a unified manner.",
    docLink: "Annotated_Chase_CC_Statement.pdf",
    modeUsed: "High Quality",
    modeId: "high_quality",
    thumbnail: AnnotatedChaseCCStatement,
  },
  {
    id: 4,
    docName: "Table extraction without line reproduction",
    description:
      "If it’s a native text PDF, just use the native text mode for extraction. It’s super fast, since it avoids computationally expensive and relatively higher latency OCR. For simple tables, there is no need to enable line reproduction in the output.",
    docLink: "SBC_Sample.pdf",
    modeUsed: "Native Text",
    modeId: "native_text",
    thumbnail: SBCSample,
  },
  {
    id: 5,
    docName: "Table extraction with vertical line reproduction",
    description:
      "ASCII line reproduction in the output can work wonders for LLM accuracy when extracting structured information since different rows become very clear to the LLM.",
    docLink: "SBC_Sample.pdf",
    modeUsed: "Low Cost",
    modeId: "low_cost",
    thumbnail: SBCSample,
    verticalLine: true,
  },
  {
    id: 6,
    docName: "Table extraction with vertical and horizontal line reproduction",
    description:
      "ASCII line reproduction in the output can work wonders for LLM accuracy when extracting structured information since different rows become very clear to the LLM.",
    docLink: "SBC_Sample.pdf",
    modeUsed: "Low Cost",
    modeId: "low_cost",
    thumbnail: SBCSample,
    verticalLine: true,
    horizontalLine: true,
  },
  {
    id: 7,
    docName: "Smartphone captured receipt",
    description:
      "A real-world use case that can be tricky to handle. LLMWhisperer’s powerful OCR engine in combination with its Layout Preservation mode can work wonders for LLM accuracy.",
    docLink: "Smartphone_Captured_Receipt.pdf",
    modeUsed: "High Quality",
    modeId: "high_quality",
    thumbnail: SmartphoneCapturedReceipt,
  },
  {
    id: 8,
    docName: "Off-orientation scans",
    description:
      "You can’t expect users to have a steady hand at all times! Off-orientation scans are more common than you think. LLMWhisperer’s powerful pre-processing handles this with elan.",
    docLink: "Off_Orientation_DL_Scan.pdf",
    modeUsed: "High Quality",
    modeId: "high_quality",
    thumbnail: OffOrientationDLScan,
  },
  {
    id: 9,
    docName: "ACORD forms",
    description:
      "The popular Acord forms are standard forms that several businesses big and small have come to rely on. They have form elements and are also electronically filled out. LLMWhisperer’s extraction quality of these forms is unmatched.",
    docLink: "Sample_Acord_Form.pdf",
    modeUsed: "High Quality with Form Elements",
    modeId: "form",
    thumbnail: SampleAcordForm,
  },
  {
    id: 10,
    docName: "Native text PDF",
    description:
      "These are the simplest documents to process under LLMWhisperer. When extracted in Native Text mode, not only is it super fast to extract, it’s also the cheapest mode to extract using!",
    docLink: "Chase_CC_Statement.pdf",
    modeUsed: "Native Text",
    modeId: "native_text",
    thumbnail: ChaseCCStatement,
  },
];

function SelectDocument({
  beforeUpload,
  onSubmit,
  uploadedDoc,
  setUploadedDoc,
}) {
  const axiosPrivate = useAxiosPrivate();
  const { sessionDetails } = useSessionStore();
  const { setAlertDetails } = useAlertStore();
  const { updateSettingsState, fileData, fileBlob, activeKey, formData } =
    useSettingsStore();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(activeKey);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [open, setOpen] = useState(false);
  const [isDocUploaded, setIsDocUploaded] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    getApiKeys().then((res) => {
      const keys = res?.data;
      const activeKeyNotInKeys = !keys?.api_keys?.some(
        (key) => key?.api_key === activeKey
      );
      const newKey = keys?.api_keys[1]?.api_key || keys?.api_keys[0]?.api_key;
      if (!activeKey || activeKeyNotInKeys) {
        setSelectedProduct(newKey);
        updateSettingsState({ activeKey: newKey });
      }
    });

    const result = {};
    docs.forEach((doc) => {
      axios(process.env.PUBLIC_URL + "/llm-whisperer/" + doc.docLink, {
        responseType: "blob",
      })
        .then((res) => {
          const blob = res.data;
          result[doc.docLink] = blob;
          updateSettingsState({ fileBlob: result });
        })
        .catch((err) => {
          api.error({
            message: "Failed to load the sample documents",
            duration: 0,
            placement: "top",
          });
        });
    });
  }, []);

  useEffect(() => {
    if (!selectedDoc?.file && uploadedDoc) {
      updateSettingsState({
        fileData: uploadedDoc,
        fileUrl: URL.createObjectURL(uploadedDoc),
      });
    }
  }, [selectedDoc?.file]);

  useEffect(() => {
    if (selectedProduct) {
      updateSettingsState({
        activeKey: selectedProduct,
      });
    }
  }, [selectedProduct]);

  useEffect(() => {
    setIsDocUploaded(uploadedDoc !== null);
  }, [uploadedDoc]);

  const updateFileContent = (doc) => {
    setOpen(true);
    updateSettingsState({
      fileUrl: URL.createObjectURL(fileBlob[doc]),
    });
  };

  const handleDeleteDoc = () => {
    if (selectedDoc) {
      setSelectedDoc(null);
      updateSettingsState({
        fileData: {},
      });
      return;
    }

    setUploadedDoc(null);
  };

  const handleSubmit = (file, options, isSample = false) => {
    const mode = options?.mode || "form";
    if (selectedProduct === null || !activeKey) {
      setAlertDetails({
        type: "error",
        content: "Please select a key",
      });
      return;
    }
    let sampleDocName;
    if (isSample) {
      sampleDocName = fileBlob[file];
    }
    if (!isDocUploaded && !file) {
      return;
    }

    if (file && typeof file === "string") {
      const blob = fileBlob[file];
      blob["name"] = file;
      const newFormData = {
        ...formData,
        processing_mode: mode,
      };
      updateSettingsState({
        fileData: blob,
        fileUrl: URL.createObjectURL(fileBlob[file]),
        formData: newFormData,
        sampleName: isSample,
      });
    } else {
      updateSettingsState({
        fileUrl: URL.createObjectURL(uploadedDoc),
        fileData: uploadedDoc,
        sampleName: false,
      });
    }

    onSubmit(mode, sampleDocName, options);
  };

  const getApiKeys = () => {
    const requestOptions = {
      method: "GET",
      url: `${LLM_WHISPERER_ENDPOINT}/keys/`,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };
    setLoadingProducts(true);
    return axiosPrivate(requestOptions)
      .then((res) => {
        const keys = res?.data;
        const activeKeyNotInKeys = !keys?.api_keys?.some(
          (key) => key?.api_key === activeKey
        );
        const newKey = keys?.api_keys[1]?.api_key || keys?.api_keys[0]?.api_key;
        if (keys?.api_keys) {
          setApiKeys(keys?.api_keys);
        }
        setLoadingProducts(false);
        if (!activeKey || activeKeyNotInKeys) {
          updateSettingsState({ activeKey: newKey });
          setSelectedProduct(newKey);
        }
        return res;
      })
      .catch(() => {
        setLoadingProducts(false);
        setAlertDetails({
          type: "error",
          content: "Failed to delete API Key",
        });
      });
  };

  const handleChange = (value) => {
    setSelectedProduct(value);
  };

  return (
    <>
      {contextHolder}
      <TopBar title="Playground">
        <Select
          className="api-key-drop-down"
          placeholder="Select a plan"
          onChange={handleChange}
          options={apiKeys.map((key) => {
            return {
              value: key?.api_key,
              label: key?.product?.name,
            };
          })}
          value={selectedProduct}
          loading={loadingProducts}
          disabled={loadingProducts}
        />
      </TopBar>
      <Flex className="select-doc-container" gap={12}>
        <div className="center doc-container">
          <Space direction="vertical" size={20}>
            <Card className="pg-info-card">
              <Typography className="pg-info-text">
                &#128075; Hello there! <br />
                Welcome to LLMWhisperer! We’ve automatically signed you up for
                the Free Plan (thought you’d like that!), which allows you to
                process 100 pages free per day! Yay!
              </Typography>
              <Typography className="pg-info-text">
                &#128187;
                <Link
                  className="pg-info-text"
                  onClick={() => {
                    navigate(
                      `/llm-whisperer/${sessionDetails?.orgName}/api-keys`
                    );
                  }}
                >
                  Feel free to create a shiny new API Key,
                </Link>{" "}
                download a Postman Collection or grab a code snippet to get
                started with the awesome LLMWhisperer API and prepare complex
                documents for LLM consumption!
              </Typography>
              <Typography className="pg-info-text">
                🛝 Want to check out LLMWhisperer capabilities? This Playground
                right here should let you do that quickly with your own
                documents. We also have some sample documents available here
                that let us show-off LLMWhisperer capabilities. Feel free to
                check them out!
              </Typography>
              <Typography className="pg-info-text">
                🛠️ We jam well with Python and Javascript! Integrate
                LLMWhisperer into your applications with our{" "}
                <Link
                  href="https://docs.unstract.com/llmwhisperer/llm_whisperer/api-client-python/index/"
                  className="pg-info-text"
                  target="_blank"
                >
                  Python
                </Link>{" "}
                and{" "}
                <Link
                  href="https://docs.unstract.com/llmwhisperer/llm_whisperer/api-client-javascript/index/"
                  className="pg-info-text"
                  target="_blank"
                >
                  Javascript
                </Link>{" "}
                clients in a snap.
              </Typography>
              <Typography className="pg-info-text">
                &#128218;{" "}
                <Link
                  href="https://docs.unstract.com/llm_whisperer/apis/index"
                  className="pg-info-text"
                  target="_blank"
                >
                  Documentation
                </Link>{" "}
                &#128108;{" "}
                <Link
                  href="https://join-slack.unstract.com/"
                  className="pg-info-text"
                  target="_blank"
                >
                  Slack Community
                </Link>
              </Typography>
            </Card>
            <div className="center">
              <Typography.Text className="select-doc-title">
                Try a sample document or upload your own
              </Typography.Text>
            </div>
            <Space className="upload-container">
              {selectedDoc || isDocUploaded ? (
                <div>
                  <Button
                    block
                    type="primary"
                    size="large"
                    className="custom-button-primary"
                    onClick={handleSubmit}
                    disabled={!selectedProduct}
                  >
                    {selectedProduct
                      ? "See Results"
                      : "Please select a key in the dropdown"}
                  </Button>
                  <div className="center" style={{ marginTop: "4px" }}>
                    {fileData?.name && (
                      <Space>
                        <Typography.Text
                          type="secondary"
                          style={{ maxWidth: "400px" }}
                          ellipsis
                        >
                          {fileData?.name}
                        </Typography.Text>
                        <Button
                          type="text"
                          size="small"
                          onClick={handleDeleteDoc}
                        >
                          <DeleteOutlined style={{ color: "grey" }} />
                        </Button>
                      </Space>
                    )}
                  </div>
                </div>
              ) : (
                <Upload
                  multiple={false}
                  maxCount={1}
                  beforeUpload={beforeUpload}
                  fileList={[]}
                  accept=".pdf"
                  className="select-doc-upload"
                >
                  <Button
                    type="primary"
                    size="large"
                    className="select-doc-upload-button"
                  >
                    Upload
                  </Button>
                </Upload>
              )}
            </Space>
            <div className="select-doc-layout center">
              <div className="sample-doc-list-container">
                <Row gutter={[16, 16]} justify={"center"}>
                  {docs.map((doc, index) => (
                    <Col key={index}>
                      <SampleDocCard
                        updateFileContent={updateFileContent}
                        description={doc.description}
                        title={doc.docName}
                        fileUrl={doc.docLink}
                        modeUsed={doc.modeUsed}
                        handleSubmit={handleSubmit}
                        modeId={doc.modeId}
                        thumbnail={doc.thumbnail}
                        id={doc.id}
                        verticalLine={doc?.verticalLine || false}
                        horizontalLine={doc?.horizontalLine || false}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          </Space>
        </div>
      </Flex>
      <PdfViewerModal open={open} setOpen={setOpen} />
    </>
  );
}

SelectDocument.propTypes = {
  beforeUpload: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  uploadedDoc: PropTypes.object,
  setUploadedDoc: PropTypes.func.isRequired,
};

export { SelectDocument };
